/* <ldy-product> (sections/ldy-main-product.liquid): gallery, options with sold-out sizes, add to bag,
   saved heart, free-shipping meter, complete the look, size guide, sticky buy bar, recently viewed.
   Data comes from the section's <script data-product-json>: v = variants [id, options, available, price, compare, mediaId, sku,
   max (stock or quantity-rule limit, 0 = none), inBag (pieces already in the bag)],
   c = color option index (-1 when none), k = product code shown before every option is picked,
   h/t = handle/title, o = per-option prompts, s = UI strings with [placeholders]. */
(() => {
  if (customElements.get('ldy-product')) return;
  const fill = (str, map) => str.replace(/\[(\w+)\]/g, (_, k) => map[k] ?? '');
  const money = (cents) => window.ldyCommerce.formatMoney(cents);
  const dash = (s) => s.replace(/-/g, '–');
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
  const calm = () => !window.ldy?.motion || matchMedia('(prefers-reduced-motion: reduce)').matches;
  const norm = (s) =>
    String(s || '')
      .toLowerCase()
      .trim();
  // Color families for "Complete the look": "Bois de rose" goes with "Pink", "Off white" with "White"…
  // Order matters: pink is tested before beige so "bois de rose" lands in pink.
  const FAMILIES = [
    ['pink', /\b(pink|rose|peach|blush|fuchsia|coral|salmon)\b/],
    ['white', /\b(white|ivory|cream|ecru)\b/],
    ['beige', /\b(beige|camel|sand|nude|taupe|brown|chocolate|caramel)\b/],
    ['grey', /\b(gr[ae]y|silver|charcoal)\b/],
    ['blue', /\b(blue|navy|aqua|turquoise|teal|denim)\b/],
    ['green', /\b(green|mint|olive|sage|khaki)\b/],
    ['red', /\b(red|burgundy|bordeaux|wine|cherry)\b/],
    ['yellow', /\b(yellow|mustard|gold|lemon)\b/],
    ['purple', /\b(purple|lilac|lavender|violet|mauve)\b/],
    ['black', /\bblack\b/],
  ];
  const family = (color) => FAMILIES.find(([, re]) => re.test(norm(color)))?.[0] || '';

  class LdyProduct extends HTMLElement {
    connectedCallback() {
      if (this.d) return; // moved in the DOM: listeners are already in place
      this.d = JSON.parse(this.querySelector('[data-product-json]').textContent);
      this.variants = this.d.v.map(([id, options, available, price, compare, media, sku, max, inBag]) => ({
        id,
        options,
        available,
        price,
        compare,
        media,
        sku,
        max,
        inBag,
      }));
      this.idInput = this.querySelector('[data-id]');
      this.groups = [...this.querySelectorAll('[data-option]')];
      const start = this.variants.find((v) => String(v.id) === this.idInput?.value) || this.variants[0];
      this.values = start.options.map((value, i) => {
        const group = this.groups.find((g) => Number(g.dataset.option) === i);
        if (!group) return value;
        return group.querySelector('[aria-pressed="true"]')?.dataset.value || '';
      });
      this.qty = 1;
      this.track = this.querySelector('[data-track]');
      this.view = this.querySelector('.ldy-gallery__view');
      this.slides = [...this.querySelectorAll('.ldy-gallery__slide')];
      this.index = Math.max(
        0,
        this.slides.findIndex((s) => s.classList.contains('is-active')),
      );
      this.slides[this.index]?.classList.add('is-active');
      this.guide = this.querySelector('[data-guide-sheet]');
      this.bar = this.querySelector('[data-buybar]');

      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('change', (e) => e.target.closest('[data-look-box]') && this.syncLook());
      this.querySelector('[data-buy]')?.addEventListener('submit', (e) => {
        e.preventDefault();
        this.add();
      });
      this.onSaved = () => this.syncSave();
      this.onCart = (e) => {
        this.paintShip(e.detail);
        this.syncBag(e.detail);
      };
      document.addEventListener('ldy:saved', this.onSaved);
      document.addEventListener('ldy:cart', this.onCart);

      this.initGallery();
      this.initBuybar();
      this.syncSave();
      this.syncLookColors();
      this.update(false);
      this.remember();
    }

    disconnectedCallback() {
      document.removeEventListener('ldy:saved', this.onSaved);
      document.removeEventListener('ldy:cart', this.onCart);
      this.observer?.disconnect();
      document.body.classList.remove('ldy-buybar-on');
    }

    onClick(e) {
      const t = e.target;
      const thumb = t.closest('[data-thumb]');
      if (thumb) return this.show(Number(thumb.dataset.thumb));
      const choice = t.closest('[data-option] [data-value]');
      if (choice && !choice.disabled)
        return this.select(Number(choice.closest('[data-option]').dataset.option), choice.dataset.value);
      const step = t.closest('[data-step]');
      if (step) return this.step(Number(step.dataset.step));
      const save = t.closest('[data-save]');
      if (save) return this.toggleSave(save);
      if (t.closest('[data-guide]')) return this.guide?.showModal();
      if (t.closest('[data-buybar-add]')) return this.add();
      if (t.closest('[data-look-add]')) return this.addLook();
      if (this.guide && (t === this.guide || t.closest('[data-close]'))) this.guide.close();
    }

    /* ---------- options ---------- */
    find(values) {
      return this.variants.find((v) => v.options.every((o, i) => o === values[i]));
    }
    byColor() {
      const c = this.d.c;
      if (c < 0) return null;
      const same = this.variants.filter((v) => v.options[c] === this.values[c]);
      return same.find((v) => v.available) || same[0] || null;
    }
    missing() {
      return this.values.findIndex((v) => v === '');
    }

    select(i, value) {
      const values = this.values.map((v, k) => (k === i ? value : v));
      if (i === this.d.c) {
        // keep the other picks only when they still exist in stock in the new color
        this.values = values.map((v, k) =>
          k === i || !v || this.variants.some((x) => x.available && x.options[i] === value && x.options[k] === v)
            ? v
            : '',
        );
      } else {
        this.values = values;
      }
      this.update(true);
      if (i === this.d.c) {
        const media = this.byColor()?.media;
        if (media) this.showMedia(media);
        this.syncLookColors();
      }
    }

    update(fromUser) {
      const s = this.d.s;
      const miss = this.missing();
      const variant = miss < 0 ? this.find(this.values) : null;
      const shown = variant || this.byColor() || this.variants.find((v) => v.available) || this.variants[0];
      this.variant = variant;
      this.setQty(this.qty); // a new size may have fewer left than the count already picked

      for (const group of this.groups) {
        const i = Number(group.dataset.option);
        const isColor = i === this.d.c;
        let sold = 0;
        for (const b of group.querySelectorAll('[data-value]')) {
          const value = b.dataset.value;
          b.setAttribute('aria-pressed', String(value === this.values[i]));
          if (isColor) continue;
          const ok = this.variants.some(
            (x) =>
              x.available &&
              x.options[i] === value &&
              x.options.every((o, k) => k === i || !this.values[k] || o === this.values[k]),
          );
          b.disabled = !ok;
          if (ok) b.removeAttribute('aria-label');
          else {
            b.setAttribute('aria-label', fill(s.soldSize, { size: dash(value) }));
            sold += 1;
          }
        }
        const label = group.querySelector('[data-value-label]');
        if (label && isColor) label.textContent = this.values[i];
        else if (label) label.innerHTML = this.values[i] ? `: <b>${esc(dash(this.values[i]))}</b>` : '';
        const note = group.querySelector('[data-note]');
        if (note) {
          const color = this.values[this.d.c];
          note.textContent = sold && color ? fill(sold === 1 ? s.soldOne : s.soldOther, { count: sold, color }) : '';
        }
      }

      const price = this.querySelector('[data-price]');
      if (price) {
        const sale = shown.compare > shown.price;
        price.innerHTML =
          `<span class="ldy-pdp__amount${sale ? ' is-sale' : ''}">${money(shown.price)}</span>` +
          (sale
            ? `<s>${money(shown.compare)}</s><span class="ldy-badge ldy-badge--sale">${fill(s.save, {
                percent: Math.round((1 - shown.price / shown.compare) * 100),
              })}</span>`
            : '');
      }

      const sku = this.querySelector('[data-sku]');
      if (sku) {
        const code = variant ? variant.sku : this.d.k;
        sku.querySelector('[data-sku-value]').textContent = code || '';
        sku.hidden = !code;
      }

      const total = money(shown.price * this.qty);
      let label = s.sold;
      let barLabel = s.sold;
      if (miss >= 0) [label, , barLabel] = this.d.o[miss];
      else if (variant?.available) {
        label = fill(s.add, { price: total });
        barLabel = fill(s.barAdd, { price: total });
      }
      const room = this.room(variant);
      // + stays tappable at the limit so step() can explain why it stops
      const plus = this.querySelector('[data-buy] [data-step="1"]');
      if (plus) plus.setAttribute('aria-disabled', String(this.qty >= room));
      const stock = this.querySelector('[data-stock]');
      if (stock) {
        const count = variant?.max;
        const one = count === 1;
        let note = '';
        if (room < 1) note = fill(one ? s.bagOne : s.bagOther, { count });
        else if (this.qty >= room) note = fill(one ? s.lastOne : s.lastOther, { count });
        stock.querySelector('[data-stock-text]').textContent = note;
        stock.dataset.over = count ? fill(one ? s.overOne : s.overOther, { count }) : '';
        stock.classList.remove('is-over');
        stock.hidden = !note;
      }
      const add = this.querySelector('[data-add]');
      if (add) {
        add.disabled = (miss < 0 && !variant?.available) || room < 1;
        if (!add.classList.contains('is-added')) this.querySelector('[data-add-label]').textContent = label;
      }
      if (variant && this.idInput) this.idInput.value = variant.id;
      // express checkout buys the form's variant, so it only shows once every option is picked
      const dyn = this.querySelector('[data-dyn]');
      if (dyn) dyn.hidden = !variant?.available;
      if (this.bar) {
        this.bar.querySelector('[data-buybar-add]').textContent = barLabel;
        this.bar.querySelector('[data-buybar-sel]').textContent = this.values.filter(Boolean).map(dash).join(', ');
      }
      if (fromUser) {
        const params = new URLSearchParams(location.search);
        if (variant) params.set('variant', variant.id);
        else params.delete('variant');
        const query = params.toString();
        history.replaceState(history.state, '', `${location.pathname}${query ? `?${query}` : ''}`);
      }
    }

    step(delta) {
      if (delta > 0 && this.qty >= this.room(this.variant))
        return window.ldyCommerce?.stockWarn(this.querySelector('[data-stock]'));
      this.setQty(this.qty + delta);
      this.update(false);
    }

    /* Pieces of a variant the guest can still add: its stock (or quantity rule) minus what is in the bag */
    room(variant) {
      return variant?.max ? variant.max - variant.inBag : Infinity;
    }

    setQty(qty) {
      this.qty = Math.max(1, Math.min(99, this.room(this.variant), qty));
      const out = this.querySelector('[data-qty]');
      const input = this.querySelector('[data-qty-input]');
      if (out) out.textContent = this.qty;
      if (input) input.value = this.qty;
    }

    syncBag(cart) {
      if (!cart?.items) return;
      const counts = cart.items.reduce(
        (m, i) => m.set(i.variant_id, (m.get(i.variant_id) || 0) + i.quantity),
        new Map(),
      );
      this.variants = this.variants.map((v) => ({ ...v, inBag: counts.get(v.id) || 0 }));
      this.update(false);
    }

    nudge(i) {
      const group = this.groups.find((g) => Number(g.dataset.option) === i);
      if (!group) return;
      const note = group.querySelector('[data-note]');
      if (note) note.textContent = this.d.o[i][1];
      const row = group.querySelector('[data-row]');
      if (row && !calm()) {
        row.animate(
          [
            { transform: 'translateX(0)' },
            { transform: 'translateX(-6px)' },
            { transform: 'translateX(6px)' },
            { transform: 'translateX(0)' },
          ],
          { duration: 300 },
        );
      }
      group.scrollIntoView({ behavior: calm() ? 'auto' : 'smooth', block: 'center' });
      group.querySelector('[data-value]:not(:disabled)')?.focus({ preventScroll: true });
    }

    async add() {
      const miss = this.missing();
      if (miss >= 0) return this.nudge(miss);
      const v = this.variant;
      if (!v?.available || this.busy || this.room(v) < 1) return;
      this.busy = true;
      const btn = this.querySelector('[data-add]');
      try {
        await window.ldyCommerce.add([{ id: v.id, quantity: this.qty }], {
          sourceImg: this.slides[this.index]?.querySelector('img'),
          openDrawer: true,
        });
        if (btn) {
          btn.classList.add('is-added');
          this.querySelector('[data-add-label]').textContent = this.d.s.added;
          setTimeout(() => {
            btn.classList.remove('is-added');
            this.update(false);
          }, 1800);
        }
      } catch (err) {
        console.error('[ldy-product] add', err); // ldyCommerce already told the guest in a toast
      } finally {
        this.busy = false;
      }
    }

    /* ---------- gallery ---------- */
    initGallery() {
      if (!this.track) return;
      // Off-screen photos wait (data-src) so the one on screen gets the whole connection.
      // Touching the gallery loads the photos either side; all of them load once the photo on screen
      // has arrived (or the page has finished loading, whichever comes first).
      this.loadSlide(this.slides[this.index]);
      const later = () => this.loadSlides();
      const first = this.slides[this.index]?.querySelector('img');
      if (document.readyState === 'complete' || !first || first.complete) setTimeout(later, 0);
      else {
        first.addEventListener('load', later, { once: true });
        first.addEventListener('error', later, { once: true });
        window.addEventListener('load', later, { once: true });
      }
      const near = () => this.loadNear(this.index);
      this.track.addEventListener('pointerdown', near, { once: true, passive: true });
      this.track.addEventListener('wheel', near, { once: true, passive: true });
      if (this.swipe() && this.index) this.track.scrollLeft = this.track.clientWidth * this.index;
      this.track.addEventListener(
        'scroll',
        () => {
          const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
          // while the track glides to a picked photo, skip the photos it passes on the way
          if (this.target != null) {
            if (i === this.target) this.target = null;
            return;
          }
          if (i !== this.index) this.show(i, false);
        },
        { passive: true },
      );
      this.track.addEventListener('scrollend', () => (this.target = null));
      if (!this.hasAttribute('data-zoom') || !matchMedia('(hover: hover) and (min-width: 990px)').matches) return;
      this.view.addEventListener('mousemove', (e) => {
        const r = this.view.getBoundingClientRect();
        const img = this.slides[this.index]?.querySelector('img');
        if (img)
          img.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
      });
      this.view.addEventListener('mouseenter', () => this.view.classList.add('is-zoom'));
      this.view.addEventListener('mouseleave', () => this.view.classList.remove('is-zoom'));
    }
    loadSlide(slide) {
      const img = slide?.querySelector('img[data-src]');
      if (!img) return;
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      img.src = img.dataset.src;
      img.removeAttribute('data-srcset');
      img.removeAttribute('data-src');
    }
    loadSlides() {
      if (this.slidesLoaded) return;
      this.slidesLoaded = true;
      this.slides.forEach((s) => this.loadSlide(s));
    }
    loadNear(i) {
      [i - 1, i + 1].forEach((k) => this.loadSlide(this.slides[k]));
    }
    swipe() {
      return this.track.scrollWidth > this.track.clientWidth + 1;
    }
    show(i, scroll = true) {
      const slide = this.slides[i];
      if (!slide) return;
      const shown = slide.querySelector('img');
      if (shown && shown.fetchPriority !== 'high') {
        shown.loading = 'eager';
        shown.fetchPriority = 'high';
      }
      this.loadSlide(slide);
      this.loadNear(i);
      this.index = i;
      if (!slide.classList.contains('is-active')) {
        slide.classList.add('is-in');
        slide.addEventListener('animationend', () => slide.classList.remove('is-in'), { once: true });
      }
      this.slides.forEach((el, k) => el.classList.toggle('is-active', k === i));
      this.querySelectorAll('[data-thumb]').forEach((b, k) => {
        b.setAttribute('aria-current', String(k === i));
        if (k !== i || !b.offsetParent) return;
        const rail = b.parentElement; // keep the active thumb in view inside the scrolling rail
        rail.scrollTo({
          top: b.offsetTop - (rail.clientHeight - b.clientHeight) / 2,
          behavior: calm() ? 'auto' : 'smooth',
        });
      });
      this.querySelectorAll('.ldy-gallery__dots i').forEach((d, k) => d.classList.toggle('is-on', k === i));
      const count = this.querySelector('[data-count]');
      if (count) count.textContent = i + 1;
      const img = slide.querySelector('img');
      const barImg = this.bar?.querySelector('img');
      if (img && barImg && (img.currentSrc || img.src)) {
        // the bar shows a 44px thumb: ask the CDN for a small copy instead of reusing the gallery file
        barImg.removeAttribute('srcset');
        barImg.src = (img.currentSrc || img.src).replace(/([?&])width=\d+/, '$1width=120');
      }
      if (scroll && this.swipe() && Math.round(this.track.scrollLeft / this.track.clientWidth) !== i) {
        this.target = i;
        this.track.scrollTo({ left: this.track.clientWidth * i, behavior: calm() ? 'auto' : 'smooth' });
      }
    }
    showMedia(id) {
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(id));
      if (i >= 0 && i !== this.index) this.show(i);
    }

    /* ---------- saved heart, free shipping, buy bar, history ---------- */
    syncSave() {
      const b = this.querySelector('[data-save]');
      if (b) b.setAttribute('aria-pressed', String((window.ldySaved?.get() || []).includes(this.d.h)));
    }
    toggleSave(b) {
      if (!window.ldySaved) return;
      const on = window.ldySaved.toggle(this.d.h);
      b.setAttribute('aria-pressed', String(on));
      if (on && !calm()) window.ldyCommerce?.burst(b);
    }

    paintShip(cart) {
      const box = this.querySelector('[data-ship]');
      if (!box || !cart) return;
      const fs = window.ldyCommerce.freeShipping(cart);
      box.hidden = !fs.enabled;
      if (!fs.enabled) return;
      const done = fs.left <= 0;
      const goal = money(fs.goal).replace(/[.,]00(?=\D*$)/, '');
      box.querySelector('[data-ship-text]').innerHTML = !fs.total
        ? fill(box.dataset.idle, { amount: goal })
        : done
          ? box.dataset.done
          : fill(box.dataset.left, { amount: money(fs.left) });
      box.classList.toggle('is-done', done);
      box.querySelector('.ldy-meter').classList.toggle('is-done', done);
      box.querySelector('.ldy-meter > i').style.setProperty('--p', fs.progress);
    }

    initBuybar() {
      const buy = this.querySelector('[data-buy]');
      if (!buy || !this.bar || !('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(([entry]) => {
        const on = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        document.body.classList.toggle('ldy-buybar-on', on);
        this.bar.inert = !on;
        this.bar.setAttribute('aria-hidden', String(!on));
      });
      this.observer.observe(buy);
    }

    remember() {
      try {
        const list = JSON.parse(localStorage.getItem('ldy-recent')) || [];
        localStorage.setItem(
          'ldy-recent',
          JSON.stringify([this.d.h, ...list.filter((h) => h !== this.d.h)].slice(0, 8)),
        );
      } catch {
        /* storage blocked: no history */
      }
    }

    /* ---------- complete the look ---------- */
    lookPicks() {
      return [...this.querySelectorAll('[data-look]:checked')].map((x) =>
        x.closest('.ldy-look__item').querySelector('[data-look-variant]'),
      );
    }
    // An item's best option for the guest's color: the same color (4), else the same family (2), else none.
    lookMatch(item, color) {
      const sel = item.querySelector('select[data-look-variant]');
      if (!sel || !color) return { bonus: 0, option: null };
      const options = [...sel.options].filter((o) => o.dataset.color);
      const exact = options.find((o) => norm(o.dataset.color) === norm(color));
      if (exact) return { bonus: 4, option: exact };
      const fam = family(color);
      const near = fam && options.find((o) => family(o.dataset.color) === fam);
      return near ? { bonus: 2, option: near } : { bonus: 0, option: null };
    }
    // Re-rank the server's candidates (snippets/ldy-look-items.liquid) by data-score + color match, show the
    // best 3 of different kinds, and switch each to the matching color. Hand-picked lists keep their order.
    syncLookColors() {
      const box = this.querySelector('[data-look-box]');
      if (!box) return;
      const color = this.d.c >= 0 ? this.values[this.d.c] : '';
      const items = [...box.querySelectorAll('.ldy-look__item')];
      const scored = items.map((el) => {
        const match = this.lookMatch(el, color);
        const total = Number(el.dataset.score || 0) + match.bonus;
        return { el, rank: Number(el.dataset.rank || 0), option: match.option, total };
      });
      const list = box.querySelector('.ldy-look__list');
      if (list && !items[0]?.hasAttribute('data-look-fixed')) {
        const kinds = new Set();
        [...scored]
          .sort((a, b) => b.total - a.total || a.rank - b.rank)
          .forEach(({ el }) => {
            const show = kinds.size < 3 && !kinds.has(el.dataset.kind);
            if (show) kinds.add(el.dataset.kind);
            el.hidden = !show;
            if (!show) el.querySelector('[data-look]').checked = false;
            list.append(el); // DOM order = visual order, so keyboard and screen readers follow the ranking
          });
      }
      for (const { option } of scored) if (option) option.selected = true;
      this.syncLook();
    }
    syncLook() {
      for (const el of this.querySelectorAll('[data-look-variant]')) {
        const item = el.closest('.ldy-look__item');
        const picked = el.selectedOptions?.[0] || el;
        const price = item?.querySelector('[data-look-price]');
        if (price) price.textContent = money(Number(picked.dataset.price || 0));
        const img = item?.querySelector('img');
        if (img && picked.dataset.img && img.getAttribute('src') !== picked.dataset.img) img.src = picked.dataset.img;
      }
      const btn = this.querySelector('[data-look-add]');
      if (!btn) return;
      const s = this.d.s;
      const picks = this.lookPicks();
      const cents = picks.reduce((sum, el) => sum + Number((el.selectedOptions?.[0] || el).dataset.price || 0), 0);
      const price = money(cents);
      btn.disabled = !picks.length;
      btn.textContent = !picks.length
        ? s.lookPick
        : fill([s.lookOne, s.lookTwo][picks.length - 1] || s.lookMany, { count: picks.length, price });
    }
    async addLook() {
      const picks = this.lookPicks();
      if (!picks.length) return;
      try {
        await window.ldyCommerce.add(
          picks.map((el) => ({ id: Number(el.value), quantity: 1 })),
          { sourceImg: picks[0].closest('.ldy-look__item').querySelector('img'), openDrawer: true },
        );
      } catch (err) {
        console.error('[ldy-product] look', err); // ldyCommerce already told the guest in a toast
      }
    }
  }

  customElements.define('ldy-product', LdyProduct);
})();
